import { useState, FormEvent } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { login } from '@/lib/auth'
import { useAuth } from '@/hooks/useAuth'
import { landingPathFor } from '@/lib/crewRouting'
import { redirectTargetFromState } from '@/lib/redirectTarget'

export function LoginPage() {
  const { isAuthenticated, user } = useAuth()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  if (isAuthenticated) {
    // A destination captured by the route guard wins: that is the AR report's
    // `/clients/{id}?tab=ar` anchor opening in a fresh tab, finding no session
    // (it is per-tab) and being sent here. Finishing that trip is the point.
    //
    // With nothing captured — an explicit logout, or /login typed directly —
    // the user's own front door decides: field crews land in the crew shell,
    // everyone else on the dashboard. Logging out must not pin you to the page
    // you happened to be on, so those sites deliberately capture nothing.
    const captured = redirectTargetFromState(location.state)
    return <Navigate to={captured ?? landingPathFor(user)} replace />
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError('')
    setIsSubmitting(true)
    try {
      await login(email, password)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="/CP_logo.png"
            alt="Clearpoint Services"
            className="h-20 w-auto mx-auto mb-4 object-contain"
          />
          <p className="text-sm text-gray-500">Sign in to your account</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="you@clearpoint.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-2.5 px-4 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
        </div>
      </div>

      {/* Footer branding */}
      <div className="mt-8 text-center">
        <p className="text-xs text-gray-400">A Clearpoint Services Proprietary Platform</p>
        <p className="text-[11px] text-gray-300 mt-0.5">Engineered &amp; Designed by Juan Rojas</p>
      </div>
    </div>
  )
}
